import { DEFAULT_LOCALE, LOCALES, type Locale } from './i18n.constants';

export function isLocale(value: unknown): value is Locale {
    return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * The locales the site serves for a given multi-language setting, which editors
 * toggle in the global site settings. With it off only the base language is
 * reachable: a language whose translation is not published yet would answer with
 * empty pages, and a crawler would index them.
 */
export function resolveActiveLocales(multiLanguageEnabled: boolean): readonly Locale[] {
    return multiLanguageEnabled ? LOCALES : [DEFAULT_LOCALE];
}

/** Narrows to a locale the site currently serves, not merely a supported one. */
export function isActiveLocale(value: unknown, activeLocales: readonly Locale[]): value is Locale {
    return isLocale(value) && (activeLocales as readonly string[]).includes(value);
}

/** Narrows an unknown value (route param, header) to a locale. */
export function toLocale(value: unknown): Locale {
    return isLocale(value) ? value : DEFAULT_LOCALE;
}

/**
 * Picks the best locale from an `Accept-Language` header.
 *
 * Quality values are honoured and language tags are matched on their primary
 * subtag, so `de-AT` and `de-CH` both resolve to `de`. Returns the base locale
 * when nothing matches — an unknown language is not a reason to send a visitor
 * to a language we cannot serve.
 *
 * `allowed` narrows the candidates to the locales the site currently serves, so
 * a deactivated language is never negotiated into.
 */
export function negotiateLocale(
    acceptLanguage: string | null | undefined,
    allowed: readonly Locale[] = LOCALES,
): Locale {
    if (!acceptLanguage) return DEFAULT_LOCALE;

    const ranked = acceptLanguage
        .split(',')
        .map((part) => {
            const [tag, ...params] = part.trim().split(';');
            const qParam = params.find((param) => param.trim().startsWith('q='));
            const quality = qParam ? Number.parseFloat(qParam.trim().slice(2)) : 1;

            return {
                primary: tag.trim().toLowerCase().split('-')[0],
                quality: Number.isFinite(quality) ? quality : 0,
            };
        })
        .filter(
            (entry): entry is { primary: Locale; quality: number } =>
                entry.quality > 0 && isActiveLocale(entry.primary, allowed),
        )
        .sort((a, b) => b.quality - a.quality);

    return ranked[0]?.primary ?? DEFAULT_LOCALE;
}

/** Splits `/de/impressum` into its locale and the remaining path. */
export function splitLocale(pathname: string): { locale: Locale | null; path: string } {
    const [, first = '', ...rest] = pathname.split('/');

    if (!isLocale(first)) return { locale: null, path: pathname };

    return { locale: first, path: `/${rest.join('/')}`.replace(/\/$/, '') || '/' };
}

/**
 * Prefixes an internal path with a locale. Absolute URLs, anchors and mail/tel
 * links pass through untouched so callers can hand it any href.
 */
export function localizePath(locale: Locale, path: string): string {
    if (!path.startsWith('/')) return path;

    const { locale: existing } = splitLocale(path);
    if (existing) return path;

    return path === '/' ? `/${locale}` : `/${locale}${path}`;
}

/**
 * The page slug a locale-stripped path addresses, or `null` for the start page.
 *
 * `/m/<slug>` is the modal route of the same document, so it resolves to the
 * same slug — a language switch from an open modal should land on that page's
 * translation, not on the start page. Anything deeper is not a page route and
 * yields `null`.
 */
export function pathToPageSlug(path: string): string | null {
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 0) return null;
    if (segments.length === 1) return segments[0];
    if (segments.length === 2 && segments[0] === 'm') return segments[1];

    return null;
}
